import React, { useEffect, useState, useMemo } from "react";
import { EthernetPort, Search, RotateCcw, ShieldCheck, ShieldAlert, Activity, ArrowUp, ArrowDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface PortInfo {
    port: number;
    state: string;
    service: string;
}

interface SwitchPortsProps {
    hideHeader?: boolean;
    viewType?: "grid" | "table";
    defaultItemsPerPage?: number;
    onDataLoaded?: (hasData: boolean) => void;
}

const SwitchPorts: React.FC<SwitchPortsProps> = ({
    hideHeader = false,
    viewType = "grid",
    defaultItemsPerPage = 10,
    onDataLoaded
}) => {
    const [portsList, setPortsList] = useState<PortInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    useEffect(() => {
        if (asset_id) fetchData();
    }, [asset_id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: asset_id, client_id: client_id })
            };

            const response = await baseapi.get("/ports", { params });
            if (response.data.status && response.data.data?.length > 0) {
                const latestEntry = response.data.data[0];
                try {
                    const parsedPorts =
                        typeof latestEntry.ports === "string"
                            ? JSON.parse(latestEntry.ports)
                            : latestEntry.ports;
                    setPortsList(parsedPorts || []);
                    if (onDataLoaded) onDataLoaded((parsedPorts || []).length > 0);
                } catch (e) {
                    console.error("Error parsing ports JSON", e);
                    setPortsList([]);
                    if (onDataLoaded) onDataLoaded(false);
                }
            } else {
                setPortsList([]);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error: any) {
            showError(error.message);
            if (onDataLoaded) onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    // Reset to page 1 when searching
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Filter data based on search
    const filteredPorts = useMemo(() => {
        return portsList.filter(p =>
            p.port?.toString().includes(searchTerm) ||
            p.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.state?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [portsList, searchTerm]);

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        setSortConfig({ key, direction: newDirection });
        setCurrentPage(1);
    };

    const sortedPorts = useMemo(() => {
        if (!sortConfig) return filteredPorts;
        const { key, direction } = sortConfig;

        return [...filteredPorts].sort((a, b) => {
            let aValue = a[key as keyof PortInfo];
            let bValue = b[key as keyof PortInfo];

            if (aValue === undefined || aValue === null) aValue = '';
            if (bValue === undefined || bValue === null) bValue = '';

            if (key === 'port') {
                return direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
            }

            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredPorts, sortConfig]);

    // Paginate filtered data
    const totalItems = filteredPorts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const paginatedPorts = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedPorts.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedPorts, currentPage, itemsPerPage]);

    const SortableHeader = ({ label, sortKey, className = "" }: { label: string; sortKey: string; className?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`px-4 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest cursor-pointer group select-none ${className}`}
            >
                <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ArrowUp size={10} className="text-orange-500" />
                            ) : (
                                <ArrowDown size={10} className="text-orange-500" />
                            )
                        ) : (
                            <ArrowDown size={10} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const renderHeader = () => (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
            <div className="flex items-center">
                <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                    <EthernetPort className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h2 className="text-sm font-bold text-white leading-tight">Open Ports</h2>
                </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-3.5 h-3.5" />
                    <input
                        type="text"
                        placeholder="Search port or service..."
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-1.5 text-[13px] text-white focus:outline-none focus:border-orange-500 transition-all font-medium"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={fetchData}
                    className="p-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-orange-500/50 transition-all group"
                    title="Refresh"
                >
                    <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                </button>
            </div>
        </div>
    );

    const renderPaginationFooter = () => {
        if (totalItems === 0) return null;
        return (
            <div className="bg-gray-900/40 px-4 py-1.5 border-t border-gray-700 flex justify-between items-center rounded-b-xl">
                <div className="flex items-center gap-3">
                    <ItemsPerPageDropdown
                        value={itemsPerPage}
                        onChange={(val) => {
                            setItemsPerPage(val);
                            setCurrentPage(1);
                        }}
                    />
                    <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                    </span>
                </div>

                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                    >
                        <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                    </button>
                    <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold leading-none flex items-center justify-center min-w-[20px]">
                        {currentPage}
                    </div>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                    >
                        <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-3 animate-fade-in">
            {!hideHeader && renderHeader()}

            {viewType === "grid" ? (
                <div className="space-y-3">
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                            {[...Array(itemsPerPage)].map((_, i) => (
                                <div key={i} className="h-20 bg-slate-800/50 rounded-xl animate-pulse border border-slate-700/50"></div>
                            ))}
                        </div>
                    ) : paginatedPorts.length === 0 ? (
                        <div className="bg-slate-900/30 rounded-xl border border-slate-800 border-dashed py-8 text-center flex flex-col items-center justify-center">
                            <EthernetPort className="w-6 h-6 text-slate-700 mb-2" />
                            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">No ports found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-2.5">
                            {paginatedPorts.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="group relative bg-slate-900 border border-slate-800 hover:border-orange-500/50 rounded-lg p-2.5 transition-all duration-300 shadow-sm overflow-hidden"
                                >
                                    <EthernetPort className="absolute -right-1 -bottom-1 w-10 h-10 text-orange-500/5 group-hover:text-orange-500/10 transition-colors" />

                                    <div className="flex justify-between items-start mb-2 relative z-10">
                                        <div className="flex flex-col">
                                            <span className="text-[8px] uppercase tracking-wider font-black text-slate-500">Port</span>
                                            <span className="text-lg font-black text-white group-hover:text-orange-500 transition-colors leading-none mt-0.5">
                                                {item.port}
                                            </span>
                                        </div>
                                        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black uppercase border ${item.state?.toLowerCase() === 'open'
                                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                            }`}>
                                            {item.state?.toLowerCase() === 'open' ? <ShieldCheck size={8} /> : <ShieldAlert size={8} />}
                                            {item.state}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-[9px] bg-slate-950/50 p-1.5 rounded-md border border-slate-800/50 relative z-10">
                                        <span className="text-slate-400 font-bold uppercase tracking-tighter truncate max-w-[70%]">
                                            {item.service || 'Unknown'}
                                        </span>
                                        <Activity size={10} className="text-orange-500/50" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {renderPaginationFooter()}
                </div>
            ) : (
                <div className="bg-slate-900/50 rounded-xl border border-slate-800 shadow-xl overflow-hidden flex flex-col">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse table-fixed">
                            <thead className="bg-slate-900/80 border-b border-slate-800">
                                <tr>
                                    <SortableHeader label="Port" sortKey="port" className="w-1/4 text-center" />
                                    <SortableHeader label="State" sortKey="state" className="w-1/4 text-center" />
                                    <SortableHeader label="Service" sortKey="service" className="w-1/2 text-center" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800/60 bg-slate-900/20">
                                {loading ? (
                                    <tr>
                                        <td colSpan={3} className="py-10 text-center text-slate-500 text-[10px] font-black uppercase tracking-widest animate-pulse">Scanning Switch Ports...</td>
                                    </tr>
                                ) : paginatedPorts.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="py-10 text-center text-slate-600 font-bold uppercase tracking-widest text-[10px]">No open ports detected</td>
                                    </tr>
                                ) : (
                                    paginatedPorts.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-slate-800/40 transition-colors group">
                                            <td className="px-4 py-1.5 text-center whitespace-nowrap">
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <div className="p-1 bg-orange-500/10 rounded group-hover:bg-orange-500/20 transition-colors">
                                                        <EthernetPort className="w-3 h-3 text-orange-500" />
                                                    </div>
                                                    <span className="text-slate-200 text-[11px] font-black font-mono">#{item.port}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-1.5 text-center whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black border uppercase ${item.state?.toLowerCase() === 'open'
                                                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                                                    : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                                                    }`}>
                                                    {item.state?.toLowerCase() === 'open' ? <ShieldCheck size={8} /> : <ShieldAlert size={8} />}
                                                    {item.state}
                                                </span>
                                            </td>
                                            <td className="px-4 py-1.5 text-center whitespace-nowrap">
                                                <span className="text-slate-300 text-[10px] font-bold tracking-tight uppercase">
                                                    {item.service || 'Unknown'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {renderPaginationFooter()}
                </div>
            )}
        </div>
    );
};

export default SwitchPorts;
